import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { OfficeCtx } from './officeCtxRef';

const hexToGradient = (hex) => {
  // Light cross-check; fall back to the default gov-blue gradient on anything odd.
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  return hex;
};

/**
 * Government-styled wrapper for the PESO Panglao portal. Carries the LGU/PESO
 * branding and the "in collaboration with Bohol Trabaho" lockup. Distinct (official
 * blue) from the main marketplace gold theme. Logo images are optional — they fall
 * back to text if the asset files aren't present yet.
 */
const LogoImg = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="h-12 w-12 rounded-full bg-white object-contain ring-1 ring-white/40"
    onError={(e) => { e.currentTarget.style.display = 'none'; }}
  />
);

const PesoLayout = ({ children, wide = false }) => {
  const { t, i18n } = useTranslation();
  const maxW = wide ? 'max-w-7xl' : 'max-w-5xl';
  const toggleLang = () => i18n.changeLanguage(i18n.language === 'ceb' ? 'en' : 'ceb');
  const office = useContext(OfficeCtx)?.office || null;

  const headerTitle = office?.name || t('peso.headerTitle', 'PESO Panglao — Public Employment Service Office');
  const headerSubtitle = office?.lgu_name || t('peso.headerSubtitle', 'Municipality of Panglao, Bohol');
  const sealSrc = office?.seal_url || '/lgu-panglao-seal.png';
  const logoSrc = office?.logo_url || '/peso-panglao-logo.png';
  const accent = hexToGradient(office?.primary_color);
  // Drive the header gradient off the office accent color when valid; else gov-blue.
  const headerStyle = accent ? { background: `linear-gradient(to right, ${accent}, ${accent}cc)` } : undefined;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Official header */}
      <header className={accent ? 'text-white' : 'bg-gradient-to-r from-blue-900 to-blue-700 text-white'} style={headerStyle}>
        <div className={`mx-auto ${maxW} px-4 py-4 flex items-center gap-3`}>
          <LogoImg src={sealSrc} alt={headerSubtitle} />
          <LogoImg src={logoSrc} alt={headerTitle} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold leading-tight truncate">
              {headerTitle}
            </p>
            <p className="text-[11px] text-blue-100 leading-tight">
              {headerSubtitle}
            </p>
          </div>
          <button
            onClick={toggleLang}
            className="shrink-0 rounded-md border border-white/40 px-2.5 py-1 text-xs font-medium hover:bg-white/10"
          >
            {i18n.language === 'ceb' ? 'English' : 'Cebuano'}
          </button>
        </div>
      </header>

      <main className="flex-1">
        <div className={`mx-auto ${maxW} px-4 py-6`}>{children}</div>
      </main>

      {/* Collaboration footer */}
      <footer className="border-t bg-white">
        <div className={`mx-auto ${maxW} px-4 py-5 text-center text-xs text-slate-500 space-y-1`}>
          <p className="font-medium text-slate-700">
            {t('peso.collabLine', 'In collaboration with Bohol Trabaho')}
          </p>
          <p>
            {office?.moa_note || t('peso.collabNote', 'Official PESO Employment System pilot — MOA signed June 4, 2026 (SB Resolution No. 119, s. 2026).')}
          </p>
          <p className="pt-1">
            <Link to="/privacy" className="underline hover:text-slate-700">{t('peso.privacy', 'Privacy Policy')}</Link>
            {' · '}
            <Link to="/terms" className="underline hover:text-slate-700">{t('peso.terms', 'Terms of Service')}</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PesoLayout;
